/**
 * Game constants and configuration
 * Centralized place for game-wide defaults and settings
 */

/**
 * Game-wide constants for timing, delays, and other configurable values
 */
export const GAME_CONSTANTS = {
  /**
   * Default starting chips for practice games
   */
  DEFAULT_STARTING_CHIPS: 10_000,

  /** Default chips granted on mid-table rebuy (private/public); matches client GAME_CONFIG rebuy amount */
  DEFAULT_MID_TABLE_REBUY_CHIPS: 10_000,

  /**
   * Default turn duration in milliseconds
   * How long human players have to make a decision before auto-fold/check
   */
  DEFAULT_TURN_DURATION: 60_000, // 60 seconds

  /**
   * Bot action delay in milliseconds
   * Bots execute actions after this delay for better UX
   * (UI still shows full turn duration countdown)
   */
  DEFAULT_BOT_MIN_DELAY: 2_000, // 2 seconds
  DEFAULT_BOT_MAX_DELAY: 5_000, // 5 seconds

  /**
   * Round progression delay in milliseconds
   * Delay before progressing to next round (deal → flop → turn → river)
   * Gives players time to see results before board changes
   */
  ROUND_PROGRESSION_DELAY: 1_000, // 1 seconds
  START_NEW_HAND_DELAY: 4_000, // 4 seconds
  /** Delay at showdown to show cards before showing winners (ms) */
  SHOWDOWN_REVEAL_DURATION: 5_000,
  WINNERS_REVEAL_DURATION: 8_000,
  FOLD_WIN_DURATION: 4_000, // Maybe use round progression?

  /**
   * Safety-valve backstop: force-end any game that reaches this many hands.
   * Guards against pause/end-condition bugs causing a game to deal hands indefinitely.
   * TODO tune once we know real hand-per-minute rates
   */
  MAX_HAND_COUNT: 500,

  /**
   * Lone-human grace window: how many hands a private/public/community table may
   * run with exactly one eligible human against bots before it auto-ends with the
   * chip leader as winner.
   *
   * Bots are legitimate opponents (so an AFK player's table stays alive while they
   * rejoin), but a table must not run forever with a single human. Hand-count
   * based on purpose — it's evaluated at the existing hand boundary, so there's no
   * cron or scheduled job, and a paused table doesn't burn the window.
   */
  LONE_HUMAN_MAX_HANDS: 100,

  /**
   * Scheduled tournament form defaults (create / start now)
   */
  DEFAULT_BLIND_TIMER_MIN: 2,
  DEFAULT_ACTION_TIME_SEC: 30,

  /**
   * Schedule retention period in days
   * How long to keep non-pending schedule records for debugging/analytics
   */
  SCHEDULE_RETENTION_DAYS: 7,

  /**
   * Scheduled game join window - before scheduled time in milliseconds
   * Users can join 5 minutes before the scheduled start
   */
  PREGAME_JOIN_WINDOW: 5 * 60 * 1_000, // 5 minutes

  /**
   * Scheduled game join window - after scheduled time in milliseconds
   * Users can join up to 1 hour after scheduled start
   */
  POST_GAME_JOIN_WINDOW: 60 * 60 * 1_000, // 1 hour

  /**
   * Lounge game join window - how long after a lounge game's designated start time
   * it remains joinable/startable. Extended from the original 15 minutes so members
   * arriving late (e.g. via an invite link) can still join. (PP-4661)
   */
  LOUNGE_GAME_JOIN_WINDOW: 15 * 60 * 1_000, // 15 minutes

  /**
   * Scheduled/lounge game join window after the scheduled start time.
   * Games (lounge schedules + "Your Scheduled Games") stay startable for ~15 min
   * after their scheduled start; afterwards they drop off the joinable lists.
   */
  SCHEDULE_JOIN_WINDOW: 15 * 60 * 1_000, // 15 minutes

  /**
   * Cron cadence for processing due schedules.
   * A due schedule stays pickable for this long after its create-time (matches the cron interval).
   */
  CRON_INTERVAL: 15 * 60 * 1_000, // 15 minutes

  /**
   * How long a recurring lounge game may sit idle (paused, no human players) before the
   * cleanup cron ends it. See cleanupAbandonedLoungeGames (Seattle lounge postmortem, PP-4698).
   */
  LOUNGE_IDLE_CLEANUP_MS: 15 * 60 * 1_000, // 15 minutes

  /** Generic duration helpers (ms), derived from DAY_MS so the relationship can never drift. */
  DAY_MS: 24 * 60 * 60 * 1_000,
  WEEK_MS: 7 * 24 * 60 * 60 * 1_000,
  TWO_WEEKS_MS: 14 * 24 * 60 * 60 * 1_000,

  /**
   * Default max players per table
   */
  DEFAULT_MAX_PLAYERS: 10,

  /**
   * Default turn duration in seconds (time per player turn)
   * Used for scheduled tournaments; maps to games.turnDuration (ms)
   */
  DEFAULT_TURN_DURATION_SEC: 30,

  /**
   * Default big blind value
   */
  DEFAULT_BIG_BLIND: 100,

  /**
   * Default minimum players to start a game
   */
  DEFAULT_MIN_PLAYERS: 2,

  /**
   * Default visibility for empty seats
   */
  DEFAULT_SHOW_EMPTY_SEATS: false,

  /**
   * Default visibility for current bets
   */
  DEFAULT_SHOW_CURRENT_BETS: false,

  /**
   * Default call to action for sharing results from games
   */
  DEFAULT_SHARE_SITE_CTA: 'Play at pokerpower.com',
  
  DEFAULT_LEADERBOARD_CAP: 100,
  /**
   * Daily 10 game mode configuration
   * Defines the core parameters for daily10 challenge games
   */
  DAILY10_CONSTANTS: {
    /**
     * Number of hands required to complete a daily10 session
     * Players must complete this many hands to finish the challenge
     */
    DEFAULT_MAX_HANDS: 10,
    DEFAULT_GAME_NAME: 'Daily 10',
    DEFAULT_GAME_NAME_PERSONALIZED: 'My Daily 10',
    DEFAULT_BIG_BLIND: 200,
    DEFAULT_SMALL_BLIND: 100,
    DEFAULT_STARTING_CHIPS: 10_000,
    DEFAULT_BOT_COUNT: 3,

    /**
     * Maximum chips achievable in daily10 mode
     * Used for calculating completion percentage and leaderboard scores
     * Based on optimal play winning every hand at escalating blinds
     */
    DEFAULT_MAX_POSSIBLE_CHIPS: 40_000,

    /**
     * Number of free preflop folds allowed
     * These folds don't count toward the hand count
     * Allows players to skip bad starting hands without penalty
     */
    DEFAULT_FREE_PREFLOP_FOLDS: 5,

    /**
     * How many times should the timers/durations be sped up?
     * E.g. 2x (1/2), 3x (1/3) of a duration
     * Default is 1 e.g. the original value.
     */
    DEFAULT_SPEEDUP: 1.5,

    NAVIGATION_THROTTLE_MS: 1_000,

    LEADERBOARD_CAP: 100
  } as const,

  /**
   * Complete the Hand configuration
   */
  CTH_CONSTANTS: {
    GAME_NAME: 'Complete the Hand',
    ROUND_COUNT: 7,
    LEADERBOARD_CAP: 100,
    MIN_SELECTION_MS: 2_000,
    NAVIGATION_THROTTLE_MS: 1_000
  } as const,

  /**
   * Pocket Pick configuration
   */
  POCKET_PICK_CONSTANTS: {
    GAME_NAME: 'Pocket Pick',
    ROUND_COUNT: 7,
    LEADERBOARD_CAP: 100,
    MIN_SELECTION_MS: 2_000,
    NAVIGATION_THROTTLE_MS: 1_000,
    CLEAR_CHOICE_MARGIN: 6, // min equity-point gap between best and 2nd-best candidate
    MAX_REROLLS: 25
  } as const
} as const
